import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ExternalLink, Pause, Pencil, Play, RotateCw, Send, ShieldCheck, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Input';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import { ROUTES } from '@/lib/routes';
import {
  useCampaignOptions,
  useOutreachActions,
  useOutreachAgent,
  useOutreachLeads,
} from './api';
import { OutreachAgentFormDialog } from './OutreachAgentFormDialog';
import { leadStatusMeta, linkedInMeta, statusMeta } from './mockData';

function formatDate(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

export default function OutreachAgentDetailPage() {
  const { id } = useParams();
  const { currentWorkspace } = useCurrentWorkspace();
  const wsId = currentWorkspace?.id;
  const agentQuery = useOutreachAgent(id, wsId);
  const leadsQuery = useOutreachLeads(wsId, { agentId: id });
  const campaignsQuery = useCampaignOptions(wsId);
  const {
    assignCampaign,
    unassignCampaign,
    pauseAgent,
    provisionAgent,
    resumeAgent,
    syncEasypanelAgents,
  } = useOutreachActions(wsId);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [showEdit, setShowEdit] = useState(false);

  const agent = agentQuery.data?.item;
  const agentLeads = leadsQuery.data?.items ?? [];

  const availableCampaigns = useMemo(() => {
    const assigned = new Set(agent?.assigned_campaigns.map((campaign) => campaign.id) ?? []);
    return (campaignsQuery.data?.items ?? []).filter((campaign) => !assigned.has(campaign.id));
  }, [agent?.assigned_campaigns, campaignsQuery.data?.items]);

  const events = useMemo(() => {
    if (!agent) return [];
    return [
      { label: 'Agent cree', detail: agent.service_name ?? agent.name, time: formatDate(agent.created_at) },
      { label: 'Derniere mise a jour', detail: agent.status, time: formatDate(agent.updated_at) },
      ...agent.assigned_campaigns.map((campaign) => ({
        label: 'Campagne assignee',
        detail: campaign.name,
        time: formatDate(campaign.assigned_at),
      })),
    ];
  }, [agent]);

  async function handleAssign() {
    if (!agent || !selectedCampaignId) return;
    try {
      await assignCampaign.mutateAsync({ agentId: agent.id, campaignId: selectedCampaignId });
      toast.success('Campagne assignee');
      setSelectedCampaignId('');
    } catch {
      toast.error("Assignation impossible");
    }
  }

  async function handleUnassign(campaignId: string) {
    if (!agent) return;
    try {
      await unassignCampaign.mutateAsync({ agentId: agent.id, campaignId });
      toast.success('Assignation retiree');
    } catch {
      toast.error("Retrait d'assignation impossible");
    }
  }

  async function togglePause() {
    if (!agent) return;
    try {
      if (agent.status === 'paused') {
        await resumeAgent.mutateAsync(agent.id);
        toast.success(`${agent.name} repris`);
      } else {
        await pauseAgent.mutateAsync(agent.id);
        toast.success(`${agent.name} en pause`);
      }
    } catch {
      toast.error("Action impossible sur l'agent");
    }
  }

  async function handleProvision() {
    if (!agent) return;
    try {
      const result = await provisionAgent.mutateAsync(agent.id);
      if (result.provisioning?.ok === false) {
        toast.error(result.provisioning.error ?? 'Provisioning Easypanel impossible');
        return;
      }
      toast.success(`${result.item.name} provisionne`);
    } catch {
      toast.error('Provisioning Easypanel impossible');
    }
  }

  async function handleSync() {
    try {
      const result = await syncEasypanelAgents.mutateAsync();
      toast.success(`${result.total} agent${result.total > 1 ? 's' : ''} synchronise${result.total > 1 ? 's' : ''}`);
    } catch {
      toast.error('Synchronisation Easypanel impossible');
    }
  }

  if (!wsId) {
    return <div className="p-8 text-gray-500">Selectionne un workspace pour ouvrir cet agent.</div>;
  }

  if (agentQuery.isLoading) {
    return <Card className="p-8 text-sm text-gray-500">Chargement de l'agent...</Card>;
  }

  if (!agent) {
    return (
      <Card>
        <EmptyState
          title="Agent introuvable"
          description="L'agent n'existe pas dans le workspace courant."
          action={
            <Link to={ROUTES.OUTREACH_AGENTS}>
              <Button variant="secondary">Retour aux agents</Button>
            </Link>
          }
        />
      </Card>
    );
  }

  const status = statusMeta[agent.status];
  const linkedIn = linkedInMeta[agent.linkedin_status];
  const provisioningError =
    typeof agent.metadata?.provisioning_error === 'string'
      ? agent.metadata.provisioning_error
      : null;
  const quotaPercent = agent.daily_limit
    ? Math.min(100, Math.round((agent.daily_used / agent.daily_limit) * 100))
    : 0;

  return (
    <div>
      <PageHeader
        title={agent.name}
        description={`${agent.owner_name ?? 'A attribuer'} - ${agent.service_name ?? `openoutreach_${agent.name}`}`}
        action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setShowEdit(true)}>
              <Pencil size={14} /> Modifier
            </Button>
            <Button variant="secondary" onClick={handleProvision} loading={provisionAgent.isPending}>
              <RotateCw size={14} /> Provisionner
            </Button>
            <Button variant="secondary" onClick={handleSync} loading={syncEasypanelAgents.isPending}>
              <RotateCw size={14} /> Synchroniser
            </Button>
            {agent.novnc_url && (
              <a href={agent.novnc_url} target="_blank" rel="noreferrer">
                <Button variant="secondary">
                  <ExternalLink size={14} /> noVNC
                </Button>
              </a>
            )}
            {agent.novnc_url ? (
              <a href={agent.novnc_url} target="_blank" rel="noreferrer">
                <Button>
                  <Send size={14} /> Connecter LinkedIn
                </Button>
              </a>
            ) : (
              <Button disabled>
                <Send size={14} /> Connecter LinkedIn
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card>
          <CardHeader title="Runtime" subtitle="Service OpenOutreach / Docker" />
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge tone={status.tone}>{status.label}</Badge>
              <Badge tone={linkedIn.tone}>LinkedIn {linkedIn.label}</Badge>
            </div>
            {provisioningError && (
              <div className="rounded-brand border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                <div className="font-semibold">Erreur provisioning Easypanel</div>
                <div className="mt-1 break-words">{provisioningError}</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Metric label="Uptime" value={agent.uptime_label ?? '-'} />
              <Metric label="Dernier signal" value={formatDate(agent.last_seen_at)} />
              <Metric label="Proxy" value={agent.proxy_country ?? '-'} />
              <Metric label="Session" value={agent.proxy_session ?? '-'} />
            </div>
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-500">Quota quotidien</span>
                <span className="font-medium text-brand-dark">
                  {agent.daily_used}/{agent.daily_limit}
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full bg-brand-primary" style={{ width: `${quotaPercent}%` }} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-brand-border">
              <Button size="sm" variant="secondary" onClick={togglePause}>
                {agent.status === 'paused' ? <Play size={12} /> : <Pause size={12} />}
                {agent.status === 'paused' ? 'Reprendre' : 'Pause'}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleProvision} loading={provisionAgent.isPending}>
                <RotateCw size={12} /> Provisionner
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Campagnes" subtitle="Assignations Dispatcher" />
          <CardContent className="space-y-4">
            {agent.assigned_campaigns.length > 0 ? (
              <div className="space-y-2">
                {agent.assigned_campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-center justify-between gap-3 rounded-brand border border-brand-border px-3 py-2"
                  >
                    <div className="min-w-0">
                      <Link
                        to={ROUTES.CAMPAIGN_DETAIL(campaign.id)}
                        className="text-sm font-semibold text-brand-dark hover:underline"
                      >
                        {campaign.name}
                      </Link>
                      <div className="text-xs text-gray-400">{campaign.assignment_status}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleUnassign(campaign.id)}
                      title="Retirer"
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title="Aucune campagne" description="Assigne cet agent a une campagne Outreach." />
            )}

            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 border-t border-brand-border pt-4">
              <Select
                value={selectedCampaignId}
                onChange={(event) => setSelectedCampaignId(event.target.value)}
                disabled={availableCampaigns.length === 0}
              >
                <option value="">Choisir une campagne</option>
                {availableCampaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </Select>
              <Button
                onClick={handleAssign}
                disabled={!selectedCampaignId}
                loading={assignCampaign.isPending}
              >
                Assigner
              </Button>
            </div>
            {availableCampaigns.length === 0 && (
              <p className="text-xs text-gray-500">
                Aucune campagne disponible. Cree une campagne ou retire une assignation existante.
              </p>
            )}
            <div className="rounded-brand bg-brand-primary-light px-3 py-3 text-xs text-brand-dark">
              <div className="flex items-center gap-2 font-semibold">
                <ShieldCheck size={14} className="text-brand-primary" /> Sync prudente
              </div>
              <p className="mt-1 text-gray-600">
                L'assignation est persistante en base. Le provisioning container sera branche sur cette
                table dans l'etape suivante.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Journal" subtitle="Evenements Dispatcher" />
          <CardContent className="space-y-3">
            {events.map((event) => (
              <div key={`${event.time}-${event.label}-${event.detail}`} className="flex gap-3">
                <div className="text-xs text-gray-400 w-24 pt-0.5">{event.time}</div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-brand-dark">{event.label}</div>
                  <div className="text-xs text-gray-500 truncate">{event.detail}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="xl:col-span-3">
          <CardHeader
            title="Leads de l'agent"
            subtitle={`${agentLeads.length} leads synchronises`}
            action={
              <Link to={ROUTES.OUTREACH_LEADS} className="text-xs font-medium text-brand-primary hover:underline">
                Tous les leads
              </Link>
            }
          />
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-brand-border">
                  <th className="th">Lead</th>
                  <th className="th">Localisation</th>
                  <th className="th">Score</th>
                  <th className="th">Statut</th>
                  <th className="th">Dernier evenement</th>
                  <th className="th">Profil</th>
                </tr>
              </thead>
              <tbody>
                {agentLeads.map((lead) => {
                  const leadStatus = leadStatusMeta[lead.status];
                  return (
                    <tr key={lead.id} className="border-b border-brand-border table-row-hover">
                      <td className="td">
                        <div className="font-semibold text-brand-dark">{lead.full_name}</div>
                        <div className="text-xs text-gray-400">
                          {lead.role_title ?? '-'} - {lead.company ?? '-'}
                        </div>
                      </td>
                      <td className="td">{lead.location ?? '-'}</td>
                      <td className="td">{lead.score}</td>
                      <td className="td"><Badge tone={leadStatus.tone}>{leadStatus.label}</Badge></td>
                      <td className="td">{lead.last_event ?? '-'}</td>
                      <td className="td">
                        {lead.profile_url ? (
                          <a href={lead.profile_url} target="_blank" rel="noreferrer" className="text-brand-primary hover:underline">
                            LinkedIn
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!leadsQuery.isLoading && agentLeads.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState title="Aucun lead" description="Les leads synchronises par cet agent apparaitront ici." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <OutreachAgentFormDialog
        open={showEdit}
        onClose={() => setShowEdit(false)}
        workspaceId={wsId}
        agent={agent}
      />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-sm font-medium text-brand-dark">{value}</div>
    </div>
  );
}
